import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { mode } from 'd3';

const CategoryTreeNode = ({ 
  node, 
  level = 0, 
  onSelect, 
  selectedId, 
  onDragStart, 
  onDrop, 
  onEdit,
  onDelete,
  onAddChild 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const hasChildren = node?.children && node?.children?.length > 0;
  const isSelected = selectedId === node?.id;

  const handleDragStart = (e) => {
    e?.stopPropagation();
    onDragStart(node);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragOver(false);
    onDrop(node);
  };

  const getNodeIcon = () => {
    switch (node?.type) {
      case 'category':
        return 'FolderOpen';
      case 'equipment':
        return 'Wrench';
      case 'problem':
        return 'AlertCircle';
      case 'service':
        return 'CheckCircle';
      default:
        return 'Circle';
    }
  };

  const getNodeColor = () => {
    switch (node?.type) {
      case 'category':
        return 'text-blue-600';
      case 'equipment':
        return 'text-purple-600';
      case 'problem':
        return 'text-orange-600';
      case 'service':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="select-none">
      <div
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 ${
          isSelected ? 'bg-primary/10 border-l-4 border-primary' : 'hover:bg-muted'
        } ${isDragOver ? 'bg-accent/20 border-2 border-dashed border-accent' : ''}`}
        style={{ paddingLeft: `${level * 20 + 12}px` }}
        onClick={() => onSelect(node)}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e?.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="p-0.5 hover:bg-muted-foreground/10 rounded"
          >
            <Icon 
              name={isExpanded ? 'ChevronDown' : 'ChevronRight'} 
              size={16} 
            />
          </button>
        )}
        
        {!hasChildren && <div className="w-4" />}

        <Icon 
          name={getNodeIcon()} 
          size={18} 
          className={getNodeColor()} 
        />

        <span className={`flex-1 text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
          {node?.name}
        </span>

        {node?.pricingModel && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            node?.pricingModel === 'fixed' ?'bg-green-100 text-green-700' :'bg-blue-100 text-blue-700'
          }`}>
            {node?.pricingModel === 'fixed' ? 'Prix fixe' : 'Sur devis'}
          </span>
        )}

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {node?.type !== 'service'   &&(
          <Button
            variant="ghost"
            size="icon"
            iconName="Plus"
            onClick={(e) => {
              e?.stopPropagation();
              onAddChild(node,'create-child');
            }}
            className="h-6 w-6"
          /> )}
          <Button
            variant="ghost"
            size="icon"
            iconName="Edit"
            onClick={(e) => {
              e?.stopPropagation();
              onEdit(node,'edit');
            }}
            className="h-6 w-6"
          />
          <Button
            variant="ghost"
            size="icon"
            iconName="Trash2"
            onClick={(e) => {
              e?.stopPropagation();
              onDelete(node);
            }}
            className="h-6 w-6 text-error hover:text-error"
          />
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {node?.children?.map((child) => (
            <CategoryTreeNode
              key={child?.id}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              selectedId={selectedId}
              onDragStart={onDragStart}
              onDrop={onDrop}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryTreeNode;